import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import Register from './pages/Register';
import Recovery from './pages/Recovery';
import Verification from './pages/Verification';
import Settings from './pages/Settings';
import OrgPicker from './pages/OrgPicker';
import Admin from './pages/Admin';
import Devices from './pages/Devices';
import Error from './pages/Error';
import MobileCallback from './pages/MobileCallback';
import MobileTokenExchange from './pages/MobileTokenExchange';
import AgentTokens from './pages/AgentTokens';
import InvitationAccept from './pages/InvitationAccept';
import Onboarding from './pages/Onboarding';
import OrgManage from './pages/OrgManage';
import Apps from './pages/Apps';
import Layout from './components/Layout';
import LoadingSpinner from './components/LoadingSpinner';

/**
 * Redirects /register/personal, /register/business, /register/developer
 * to /register?profile=<profile>, preserving any existing query params.
 */
function RegisterProfileRedirect({ profile }: { profile: string }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('profile', profile);
  return <Navigate to={`/register?${params.toString()}`} replace />;
}

function HomeRedirect() {
  const { isAuthenticated, isLoading, organizations } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // New users with no org need guidance — send to onboarding
  if (organizations.length === 0) {
    return <Navigate to="/onboarding" replace />;
  }

  return <Navigate to="/apps" replace />;
}

function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route element={<Layout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/register/personal" element={<RegisterProfileRedirect profile="individual" />} />
          <Route path="/register/business" element={<RegisterProfileRedirect profile="business" />} />
          <Route path="/register/developer" element={<RegisterProfileRedirect profile="developer" />} />
          <Route path="/recovery" element={<Recovery />} />
          <Route path="/verification" element={<Verification />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/organizations" element={<OrgPicker />} />
          <Route path="/devices" element={<Devices />} />
          <Route path="/agent-tokens" element={<AgentTokens />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/mobile-callback" element={<MobileCallback />} />
          <Route path="/mobile-token-exchange" element={<MobileTokenExchange />} />
          <Route path="/invitations/accept" element={<InvitationAccept />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/org" element={<OrgManage />} />
          <Route path="/apps" element={<Apps />} />
          <Route path="/error" element={<Error />} />
          <Route path="/" element={<HomeRedirect />} />
          <Route path="*" element={<Navigate to="/error" replace />} />
        </Route>
      </Routes>
    </AuthProvider>
  );
}

export default App;
